import clsx from 'clsx';
import Avatar from '../Avatar/Avatar';
import ItemComplexListBase from 'core/modules/components/general/ItemComplexList';
import React from 'react';
import { Picky } from 'react-picky';
import 'react-picky/dist/picky.css';
import Icon from '../Icon/Icon';
import InputCheckbox from '../InputCheckbox/InputCheckbox';
import type { PickyRenderItemProps, PickyRenderSelectAllProps } from './useSelectCheckboxs';
import useSelectCheckboxs from './useSelectCheckboxs';

type ItemComplexListProps = {
  center?: React.ReactNode;
  className?: string;
  isEllipsis?: boolean;
  left?: React.ReactNode;
  onClick?: () => void;
  previewContent?: React.ReactNode;
  right?: React.ReactNode;
};
const ItemComplexList = ItemComplexListBase as React.ComponentType<ItemComplexListProps>;

export type SelectCheckboxOption = {
  disabled?: boolean;
  thumbnail?: string;
  [key: string]: unknown;
};

type PickyComponentProps = {
  allSelectedPlaceholder: React.ReactNode;
  className?: string;
  defaultFocusFilter?: boolean;
  disabled?: boolean;
  dropdownHeight?: number;
  filterPlaceholder?: string;
  includeFilter?: boolean;
  includeSelectAll?: boolean;
  labelKey?: string;
  manySelectedPlaceholder?: string;
  multiple?: boolean;
  numberDisplayed?: number;
   
  onChange: (value: SelectCheckboxOption | SelectCheckboxOption[]) => void;
  onClose?: () => void;
  onOpen?: () => void;
  options: SelectCheckboxOption[];
  placeholder?: React.ReactNode;
   
  render?: (props: PickyRenderItemProps) => React.ReactElement;
   
  renderSelectAll?: (props: PickyRenderSelectAllProps) => React.ReactElement | null;
  value: SelectCheckboxOption[];
  valueKey?: string;
};
const PickyTyped = Picky as unknown as React.ComponentType<PickyComponentProps>;

type SelectCheckboxsProps = Readonly<{
  buttonAllText?: React.ReactNode;
  disabled?: boolean;
  dropdownHeight?: number;
  filterPlaceholder?: string;
  hasAvatar?: boolean;
  hasFilter?: boolean;
  hasSelectAll?: boolean;
  isError?: boolean;
  isMultiple?: boolean;
  label?: React.ReactNode;
  labelKey?: string;
  numberDisplayed?: number;
   
  onChange?: (value: SelectCheckboxOption[]) => void;
  options?: SelectCheckboxOption[];
  placeholder?: string;
  selectAllText?: React.ReactNode;
  shouldDebounce?: boolean;
  value?: SelectCheckboxOption[];
  valueKey?: string;
}>;

function SelectCheckboxs({
  buttonAllText,
  disabled = false,
  dropdownHeight = 350,
  filterPlaceholder,
  hasAvatar = false,
  hasFilter = true,
  hasSelectAll = true,
  isError,
  isMultiple = true,
  label,
  labelKey = 'label',
  numberDisplayed = 3,
  onChange = () => {},
  options = [],
  placeholder,
  selectAllText,
  shouldDebounce = true,
  value = [],
  valueKey = 'value',
}: SelectCheckboxsProps): React.ReactElement {
  const {
    allItemsSelected,
    containerRef,
    focusedOptionIndex,
    handleClose,
    handleOpen,
    handleOptionKeyDown,
    labelRef,
    onChangeState,
    optionIndexMap,
    optionRefs,
    setFocusedOptionIndex,
    t,
    valueState,
  } = useSelectCheckboxs({
    disabled,
    isError,
    label,
    labelKey,
    onChange,
    options,
    shouldDebounce,
    value,
    valueKey,
  });

  function renderOption({ isSelected, item, selectValue }: PickyRenderItemProps): React.ReactElement {
    const itemIndex = optionIndexMap.get(item[valueKey]) ?? -1;
    const isFocused = focusedOptionIndex === itemIndex;

    function handleClick(): void {
      if (!item.disabled) selectValue(item);
    }

    function handleFocus(): void {
      if (!item.disabled) setFocusedOptionIndex(itemIndex);
    }

    function handleKeyDown(event: React.KeyboardEvent): void {
      handleOptionKeyDown(event, item, selectValue);
    }

    return (
      <div key={String(item[valueKey] as string | number)} className="select-checkboxs__option-container">
        {/* NOSONAR - role="option" is the correct ARIA pattern for custom listbox items; <option> requires <select> */}<div
          ref={(el) => {
            optionRefs.current[itemIndex] = el;
          }}
          aria-disabled={item.disabled ? 'true' : 'false'}
          aria-selected={isSelected}
          className={clsx('select-checkboxs__option', {
            'select-checkboxs__option--disabled': item.disabled,
            'select-checkboxs__option--focused': isFocused,
            'select-checkboxs__option--selected': isSelected,
          })}
          onClick={handleClick}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          role="option"
          tabIndex={item.disabled ? -1 : 0}
        >
          <ItemComplexList
            center={
              <span className="select-checkboxs__option-text">
                {String((item[labelKey] as string | number) ?? '')}
              </span>
            }
            left={
              hasAvatar ? (
                <div>
                  <Avatar image={item.thumbnail} size="sm" />
                </div>
              ) : undefined
            }
            right={
              <InputCheckbox
                checked={isSelected}
                disabled={item.disabled}
              />
            }
          />
        </div>
      </div>
    );
  }

  function renderSelectAll({
    allSelected,
    filtered,
    multiple: pickyMultiple,
    tabIndex,
    toggleSelectAll,
  }: PickyRenderSelectAllProps): React.ReactElement | null {
    const all = allSelected === 'all';
    if (!pickyMultiple || filtered) return null;

    return (
      <button
        aria-pressed={all}
        className={clsx('select-checkboxs__all-container', {
          'option selected picky__all-div': all,
          option: !all,
        })}
        onClick={toggleSelectAll}
        tabIndex={tabIndex}
        type="button"
      >
        <span
          className={clsx('select-checkboxs__all', { 'select-checkboxs__all--active': all })}
        >
          {buttonAllText ?? t('All')}
        </span>
      </button>
    );
  }

  const allSelectedLabel = selectAllText ?? t('All selected');
  const allPlaceholder = (
    <div className="select-checkboxs__placeholder-all">
      <Icon name="group" size="lg"/>
      <span className="select-checkboxs__placeholder-all-text">{allSelectedLabel}</span>
    </div>
  );
  const placeholderNormal = placeholder ?? t('placeholder:Select');

  return (
    <div
      ref={containerRef}
      className={clsx('select-checkboxs', { 'select-checkboxs--error': isError })}
    >
      {label && <label ref={labelRef} className="select-checkboxs__label">{label}</label>}

      <PickyTyped
        allSelectedPlaceholder={allPlaceholder}
        className="select-checkboxs__picky"
        defaultFocusFilter
        disabled={disabled}
        dropdownHeight={dropdownHeight}
        filterPlaceholder={filterPlaceholder ?? t('placeholder:Search')}
        includeFilter={hasFilter}
        includeSelectAll={hasSelectAll}
        labelKey={labelKey}
        manySelectedPlaceholder={`%s ${t('Selected').toLowerCase()}`}
        multiple={isMultiple}
        numberDisplayed={allItemsSelected ? 0 : numberDisplayed}
        onChange={onChangeState}
        onClose={handleClose}
        onOpen={handleOpen}
        options={options}
        placeholder={options.length === 0 ? allPlaceholder : placeholderNormal}
        render={renderOption}
        renderSelectAll={renderSelectAll}
        value={valueState}
        valueKey={valueKey}
      />
    </div>
  );
}

export default SelectCheckboxs;
